import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  List,
  Stack,
  Dialog,
  Avatar,
  Button,
  Checkbox,
  ListItem,
  TextField,
  Typography,
  IconButton,
  DialogTitle,
  DialogContent,
} from '@mui/material';

import { useAuth } from 'src/hooks/useAuth';

import Iconify from 'src/components/iconify';

import { useToast } from '../../../hooks/useToast';
import convertToBase64 from '../../../utils/convertBase64';
import compressImage from '../../../utils/imageCompressor';
import ImageInput from '../../../components/inputs/ImageInput';
import { bulkAssignMembers } from '../../../services/department.service';
import { getUserAvatarUrl, getUniversalCandidates } from '../../../services/user.service';

DepartmentFormModal.propTypes = {
  department: PropTypes.object,
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  apiCall: PropTypes.func,
};

const maxSize = 1024 * 1024 * 2;
const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

export default function DepartmentFormModal({ department, open, setOpen, apiCall }) {
  const { t } = useTranslation();
  const { id: currentUserId } = useAuth();
  const { showToast, hideToast } = useToast();

  const [activeStep, setActiveStep] = useState(0);
  const [candidates, setCandidates] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid },
  } = useForm({
    mode: 'all',
    defaultValues: {
      id: department?.id || '',
      name: department?.name || '',
      description: department?.description || '',
      image: department?.image || '',
      createdBy: department?.createdBy || '',
    },
  });

  const watchedValues = watch();

  const isFormDirty = useMemo(
    () =>
      watchedValues.name !== department?.name ||
      watchedValues.description !== department?.description ||
      selectedFile !== null,
    [watchedValues, department, selectedFile]
  );

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (allowedTypes.includes(file.type) && file.size <= maxSize) {
        setSelectedFile(file);
      } else {
        setSelectedFile(null);
        showToast({
          message: t('formControl.validImage'),
          severity: 'error',
          props: { hideToast },
        });
      }
    }
  };

  const handleClose = () => {
    reset();
    setSelectedFile(null);
    setActiveStep(0);
    setCandidates([]);
    setSelectedUserIds([]);
    setOpen(false);
  };

  const handleProceedToSync = async (data) => {
    if (department) {
      executeFinalSubmit(data);
      return;
    }

    setIsProcessing(true);
    try {
      const users = await getUniversalCandidates();
      if (users && users.length > 0) {
        setCandidates(users);
        setSelectedUserIds(users.map((u) => u.id));
        setActiveStep(1);
      } else {
        executeFinalSubmit(data);
      }
    } catch (e) {
      executeFinalSubmit(data);
    } finally {
      setIsProcessing(false);
    }
  };

  const executeFinalSubmit = async (formData) => {
    setIsProcessing(true);

    const finalize = async (imageB64) => {
      const fullData = {
        ...formData,
        createdBy: department ? formData.createdBy : currentUserId,
        image: imageB64,
      };

      try {
        const createdDept = await apiCall(fullData);

        if (selectedUserIds.length > 0 && createdDept?.id) {
          await bulkAssignMembers(createdDept.id, {
            userIds: selectedUserIds,
            groupIds: [],
          });
        }

        handleClose();
      } catch (e) {
        console.error('Finalization failed:', e);
      } finally {
        setIsProcessing(false);
      }
    };

    if (selectedFile) {
      if (selectedFile.type === 'image/gif') convertToBase64(selectedFile).then(finalize);
      else compressImage(selectedFile, finalize);
    } else {
      finalize(department?.image || '');
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((i) => i !== userId) : [...prev, userId]
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: '24px',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: (theme) =>
            theme.palette.mode === 'dark'
              ? 'rgba(22, 27, 34, 0.9)'
              : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary' }}>
            {activeStep === 0
              ? department
                ? t('pages.departmentPage.form.modifyDepartment')
                : t('pages.departmentPage.form.createDepartment')
              : t('permissionss.sync.handshakeTitle')}
          </Typography>
          <IconButton onClick={handleClose} sx={{ mr: -1.5 }}>
            <Iconify icon="eva:close-square-fill" width={30} />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {activeStep === 0 ? (
          <form onSubmit={handleSubmit(handleProceedToSync)}>
            <Stack spacing={3} mt={1}>
              <TextField
                fullWidth
                name="name"
                label={t('attributes.name')}
                error={!!errors.name}
                helperText={errors.name?.message}
                {...register('name', {
                  required: t('formControl.enterName'),
                  minLength: 4,
                })}
              />
              <TextField
                fullWidth
                multiline
                minRows={4}
                name="description"
                label={t('attributes.description')}
                {...register('description')}
              />
              <ImageInput
                handleChange={handleFileChange}
                selectedFile={selectedFile}
                existingFile={department?.image}
                defaultImage="/assets/images/departments/defaultDepartment.png"
              />
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <LoadingButton
                  fullWidth
                  variant="contained"
                  loading={isProcessing}
                  disabled={!isValid || !isFormDirty}
                  type="submit"
                  sx={{
                    py: 1.5,
                    fontWeight: 800,
                    borderRadius: '12px',
                    bgcolor: '#00A76F',
                    '&:hover': { bgcolor: '#008F5D' },
                  }}
                >
                  {department ? t('buttons.update') : t('buttons.next')}
                </LoadingButton>
              </Stack>
            </Stack>
          </form>
        ) : (
          <Box>
            <Typography
              variant="body2"
              sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.6 }}
            >
              {t('permissionss.sync.universalNote')} <b>{watchedValues.name}</b>.
            </Typography>

            <Box
              sx={{
                bgcolor: 'background.neutral',
                borderRadius: '16px',
                border: '1px solid',
                borderColor: 'divider',
                p: 1,
              }}
            >
              <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                {candidates.map((user) => (
                  <ListItem
                    key={user.id}
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2} width="100%">
                      <Checkbox
                        checked={selectedUserIds.includes(user.id)}
                        onChange={() => toggleUserSelection(user.id)}
                        sx={{
                          color: '#00A76F',
                          '&.Mui-checked': { color: '#00A76F' },
                        }}
                      />
                      <Avatar
                        src={
                          user.avatarVersion
                            ? getUserAvatarUrl(user.id, user.avatarVersion)
                            : null
                        }
                        imgProps={{ loading: 'lazy' }}
                        sx={{
                          width: 36,
                          height: 36,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      />
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                          {user.givenName} {user.familyName}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.disabled', display: 'block' }}
                        >
                          {user.email}
                        </Typography>
                      </Box>
                    </Stack>
                  </ListItem>
                ))}
              </List>
            </Box>

            <Stack spacing={1.5} sx={{ mt: 4 }}>
              <LoadingButton
                fullWidth
                variant="contained"
                loading={isProcessing}
                onClick={() => executeFinalSubmit(watchedValues)}
                sx={{
                  py: 1.5,
                  fontWeight: 800,
                  borderRadius: '12px',
                  bgcolor: '#00A76F',
                  '&:hover': { bgcolor: '#008F5D' },
                }}
              >
                {t('buttons.confirmCreate')}
              </LoadingButton>
              <Button
                fullWidth
                onClick={() => setActiveStep(0)}
                disabled={isProcessing}
                color="inherit"
                sx={{ fontWeight: 700 }}
              >
                {t('buttons.back')}
              </Button>
            </Stack>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
